import { useState, useRef, useCallback } from "react";
import { MoveHorizontal, MapPin } from "lucide-react";
import { GALLERY } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";

const Comparison = ({ item }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  }, []);

  const onDown = () => (dragging.current = true);
  const onUp = () => (dragging.current = false);
  const onMove = (e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    setFromClientX(clientX);
  };

  return (
    <div
      ref={ref}
      data-testid={`gallery-item-${item.id}`}
      className="relative rounded-2xl overflow-hidden border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] aspect-[4/3] select-none cursor-ew-resize"
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchMove={onMove}
      onTouchEnd={onUp}
    >
      <img src={item.after} alt={`${item.title} after`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      <span className="absolute bottom-4 right-4 z-10 bg-brick text-white text-xs font-bold px-3 py-1 rounded-full">AFTER</span>

      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={item.before}
          alt={`${item.title} before`}
          loading="lazy"
          className="absolute inset-0 h-full object-cover max-w-none"
          style={{ width: ref.current ? ref.current.offsetWidth : "100%" }}
        />
        <span className="absolute bottom-4 left-4 bg-charcoal/85 text-white text-xs font-bold px-3 py-1 rounded-full">BEFORE</span>
      </div>

      <div className="absolute top-0 bottom-0 z-20" style={{ left: `${pos}%`, transform: "translateX(-50%)" }}>
        <div className="w-0.5 h-full bg-white shadow-lg" />
        <button
          onMouseDown={onDown}
          onTouchStart={onDown}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white shadow-xl flex items-center justify-center cursor-ew-resize"
          aria-label="Drag to compare"
        >
          <MoveHorizontal className="w-5 h-5 text-brick" />
        </button>
      </div>

      <div className="absolute top-4 left-4 z-10">
        <p className="font-heading font-bold text-white text-lg drop-shadow-lg">{item.title}</p>
        <p className="flex items-center gap-1 text-white/90 text-xs drop-shadow"><MapPin className="w-3 h-3" /> {item.location}</p>
      </div>
    </div>
  );
};

export const BeforeAfter = () => {
  return (
    <section id="gallery" data-testid="gallery-section" className="bg-white py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <Reveal>
          <span className="text-brick font-bold text-sm tracking-[0.18em] uppercase">Our Work</span>
          <h2 className="mt-3 font-heading font-extrabold tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl max-w-3xl leading-[1.03]">
            Before &amp; after transformations.
          </h2>
          <p className="mt-4 text-charcoal/60 max-w-xl text-base sm:text-lg">
            Drag the slider on each project to see the difference quality masonry makes.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {GALLERY.map((item, i) => (
            <Reveal key={item.id} delay={i * 0.1}>
              <Comparison item={item} />
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-charcoal/40 text-xs">* Slide the handle to compare real project work. Some “before” reference photos shown for illustration.</p>
      </div>
    </section>
  );
};
